import { useEffect } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Space, Spin, message } from 'antd';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { projectsApi, type ProjectInput } from '../../../api/endpoints/projects';
import { AccountSelect } from '../../../components/common/AccountSelect';
import { PersonSelect } from '../../../components/common/PersonSelect';
import { UserSelect } from '../../../components/common/UserSelect';
import { PageHeader } from '../../../components/common/PageHeader';
import { LookupAutoComplete } from '../../../components/common/LookupAutoComplete';

const schema = z.object({
  name: z.string().min(1, 'Nombre obligatorio').max(120),
  projectNumber: z.string().max(40).optional().or(z.literal('')),
  customerId: z.number().int().optional().nullable(),
  primaryPersonId: z.number().int().optional().nullable(),
  assignedToId: z.number().int().optional().nullable(),
  status: z.string().max(40).optional().or(z.literal('')),
  startDate: z.string().optional().or(z.literal('')),
  endDate: z.string().optional().or(z.literal('')),
  amount: z.string().optional().or(z.literal('')),
  plannedHours: z.number().int().optional().nullable(),
  spentHours: z.number().int().optional().nullable(),
  notes: z.string().optional().or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

function sanitize(values: FormValues): ProjectInput {
  const out: Partial<ProjectInput> = { name: values.name };
  if (values.projectNumber) out.projectNumber = values.projectNumber;
  if (values.customerId != null) out.customerId = values.customerId;
  if (values.primaryPersonId != null) out.primaryPersonId = values.primaryPersonId;
  if (values.assignedToId != null) out.assignedToId = values.assignedToId;
  if (values.status) out.status = values.status;
  if (values.startDate) out.startDate = values.startDate;
  if (values.endDate) out.endDate = values.endDate;
  if (values.amount) out.amount = values.amount;
  if (values.plannedHours != null) out.plannedHours = values.plannedHours;
  if (values.spentHours != null) out.spentHours = values.spentHours;
  if (values.notes) out.notes = values.notes;
  return out as ProjectInput;
}

export function ProjectEditPage(): JSX.Element {
  const { id } = useParams();
  const projectId = id ? parseInt(id, 10) : null;
  const isEdit = projectId !== null;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const presetCustomerId = searchParams.get('customerId');

  const { data, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.findOne(projectId!),
    enabled: isEdit,
  });

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      projectNumber: '',
      customerId: presetCustomerId ? parseInt(presetCustomerId, 10) : null,
      primaryPersonId: null,
      assignedToId: null,
      status: '',
      startDate: '',
      endDate: '',
      amount: '',
      plannedHours: null,
      spentHours: null,
      notes: '',
    },
  });

  const watchedCustomerId = useWatch({ control, name: 'customerId' });

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        projectNumber: data.projectNumber ?? '',
        customerId: data.customerId,
        primaryPersonId: data.primaryPersonId,
        assignedToId: data.assignedToId,
        status: data.status ?? '',
        startDate: data.startDate ?? '',
        endDate: data.endDate ?? '',
        amount: data.amount ?? '',
        plannedHours: data.plannedHours,
        spentHours: data.spentHours,
        notes: data.notes ?? '',
      });
    }
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = sanitize(values);
      if (isEdit) return projectsApi.update(projectId, payload);
      return projectsApi.create(payload);
    },
    onSuccess: (saved) => {
      message.success(isEdit ? 'Proyecto actualizado' : 'Proyecto creado');
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['project', saved.id] });
      navigate(`/proyectos/${saved.id}`);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No se pudo guardar el proyecto';
      message.error(msg);
    },
  });

  if (isEdit && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={isEdit ? 'Editar proyecto' : 'Nuevo proyecto'}
        crumbs={[
          { title: 'Proyectos', to: '/proyectos' },
          { title: isEdit ? data?.name ?? '' : 'Nuevo' },
        ]}
      />

      <Form layout="vertical" onFinish={handleSubmit((v) => mutation.mutate(v))}>
        <Card title="Datos generales" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Controller
                name="projectNumber"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Número de proyecto">
                    <Input {...field} placeholder="Ej. P-2026-001" />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={16}>
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <Form.Item
                    label="Nombre"
                    required
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input {...field} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="customerId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Cuenta cliente">
                    <AccountSelect value={field.value} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="primaryPersonId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Contacto principal">
                    <PersonSelect
                      value={field.value}
                      onChange={field.onChange}
                      accountId={watchedCustomerId ?? undefined}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="assignedToId"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Responsable">
                    <UserSelect value={field.value} onChange={field.onChange} />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Estado">
                    <LookupAutoComplete
                      listName="project_status"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Ej. En progreso, En pausa…"
                    />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Fechas y montos" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Inicio">
                    <DatePicker
                      style={{ width: '100%' }}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={12}>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Fin">
                    <DatePicker
                      style={{ width: '100%' }}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={24} md={8}>
              <Controller
                name="amount"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Monto">
                    <Input {...field} placeholder="0.00" inputMode="decimal" />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={12} md={8}>
              <Controller
                name="plannedHours"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Horas planeadas">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      value={field.value ?? undefined}
                      onChange={(v) => field.onChange(v ?? null)}
                    />
                  </Form.Item>
                )}
              />
            </Col>
            <Col xs={12} md={8}>
              <Controller
                name="spentHours"
                control={control}
                render={({ field }) => (
                  <Form.Item label="Horas usadas">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      value={field.value ?? undefined}
                      onChange={(v) => field.onChange(v ?? null)}
                    />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Notas" style={{ marginBottom: 16 }}>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <Form.Item>
                <Input.TextArea {...field} rows={4} />
              </Form.Item>
            )}
          />
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            {isEdit ? 'Guardar cambios' : 'Crear proyecto'}
          </Button>
          <Button onClick={() => navigate(-1)}>Cancelar</Button>
        </Space>
      </Form>
    </>
  );
}
